import type { Amount } from '@klareospace/pharos-lib-types';
import { EMPTY_VALUE_PLACEHOLDER } from '../constants';

/**
 * Format an Amount value for display using the given locale's currency formatting.
 * Returns '—' (em dash) when the amount is undefined.
 *
 * @example formatPrice({ currency: 'EUR', value: 12.5 }, 'en')  // '€12.50'
 * @example formatPrice(undefined, 'en')                          // '—'
 */
export function formatPrice(price: Amount | undefined, locale: string): string {
  if (price === undefined) return EMPTY_VALUE_PLACEHOLDER;
  return new Intl.NumberFormat(locale, { currency: price.currency, style: 'currency' }).format(price.value);
}

/**
 * Resolve the display symbol for an ISO currency code (e.g. 'EUR' -> '€', 'USD' -> '$').
 * Falls back to the code itself if the locale/currency combination yields no currency part.
 *
 * @example getCurrencySymbol('EUR', 'en')  // '€'
 * @example getCurrencySymbol('USD', 'fr')  // '$'
 */
export function getCurrencySymbol(currency: string, locale: string): string {
  const parts = new Intl.NumberFormat(locale, { currency, style: 'currency' }).formatToParts(0);
  const currencyPart = parts.find((part) => part.type === 'currency');
  return currencyPart?.value ?? currency;
}
